// Messages between the page's extension bridge and the tRPC debugger browser
// extension's content script. Both sides talk over `window.postMessage` on
// the page's own window, so the payload is structured-cloned. Keep this file
// dependency-free: the extension imports it, and other apps copy it verbatim.
import type { TrpcDebugEvent } from './types'

/** Bump when a message shape changes; each side ignores other versions. */
export const TRPC_EXTENSION_PROTOCOL_VERSION = 1

export const TRPC_PAGE_SOURCE = 'trpc-debugger:page'
export const TRPC_EXTENSION_SOURCE = 'trpc-debugger:extension'

type Envelope<Source extends string> = { source: Source; version: number }

export type TrpcPageBody =
	/** The bridge is installed (the content script, at document_start, is already listening). */
	| { type: 'ready' }
	/** A debug event whose values went through `toCloneSafeEvent`. */
	| { type: 'event'; event: TrpcDebugEvent }

/** Start (`connect`) or stop (`disconnect`) streaming events. */
export type TrpcExtensionBody = { type: 'connect' } | { type: 'disconnect' }

export type TrpcPageMessage = Envelope<typeof TRPC_PAGE_SOURCE> & TrpcPageBody
export type TrpcExtensionMessage = Envelope<typeof TRPC_EXTENSION_SOURCE> & TrpcExtensionBody

function hasSource(data: unknown, source: string): boolean {
	return (
		typeof data === 'object' &&
		data !== null &&
		(data as { source?: unknown }).source === source &&
		(data as { version?: unknown }).version === TRPC_EXTENSION_PROTOCOL_VERSION
	)
}

export function isTrpcPageMessage(data: unknown): data is TrpcPageMessage {
	return hasSource(data, TRPC_PAGE_SOURCE)
}

export function isTrpcExtensionMessage(data: unknown): data is TrpcExtensionMessage {
	return hasSource(data, TRPC_EXTENSION_SOURCE)
}
